import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import DOMPurify from 'dompurify'
import { useI18n } from 'vue-i18n-composable'
import { processMarkdown } from '@/helpers/markdown'
import { getChunkById, getChunkTooltipHTML } from '@/helpers/chunk'
import { useAdvanceSearchTooltip } from '@/modules/documentSearcher/composables/useAdvanceSearchTooltip'
import SearchIAToolbarComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIAToolbarComponent.vue'
import SearchIARelatedDocumentsComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIARelatedDocumentsComponent.vue'

// DEFINE PROPS

export default /*#__PURE__*/_defineComponent({
  __name: 'SearchIAResultComponent',
  props: {
    results: { type: null, required: true }
  },
  setup(__props: any) {

const props = __props as {
  results: any
};



// TRANSLATIONS
const { t } = useI18n()

const TEXT = {
  response: t('views.search_ia_result.response') as string,
  legal: t('views.search_ia_result.legal') as string
}

// DATA
const ICON = require('@/assets/images/svg/icon-chevron-up.svg')
const infoIcon = require('@/assets/images/svg/icon-info.svg')
const isResultsExpanded = ref(true)
const CHUNK_ID_REGEX = /\[\s*CHUNK_ID\s*:\s*([^\]\s]+)\s*\]/g
// Usar el composable de tooltip
useAdvanceSearchTooltip()

// COMPUTED
// Crear un mapa de chunk_id a número autonumérico basado en los chunks que aparecen en generated_response
// Garantiza que cada chunk_id único tenga siempre el mismo número
const chunkIdMap = computed(() => {
  const map = new Map<string, number>()
  if (!props.results || !props.results.generated_response) {
    return map
  }

  // Extraer todos los chunk_ids que aparecen en generated_response
  const matches = [...props.results.generated_response.matchAll(CHUNK_ID_REGEX)]

  let counter = 1
  matches.forEach((match) => {
    const chunkId = match[1]
    if (chunkId && !map.has(chunkId)) {
      // Solo asignar número si el chunk_id no existe ya en el map
      map.set(chunkId, counter)
      counter++
    }
  })

  return map
})

const renderedContent = computed(() => {
  if (!props.results || !props.results.generated_response) {
    return ''
  }

  // Procesar el contenido principal como markdown
  const htmlContent = processMarkdown(props.results.generated_response)

  // Reemplazar los chunk IDs en el HTML generado
  const htmlWithChunks = htmlContent.replace(CHUNK_ID_REGEX, (match: string, chunkId: string) => {
    // Obtener el chunk correspondiente
    const chunk = getChunkById(props.results.retrieved_chunks, chunkId)
    if (!chunk) return match

    // Obtener el número del chunk desde el mapa
    const chunkNumber = chunkIdMap.value.get(chunkId) || '?'

    // Crear HTML para el tooltip con el número en lugar del icono
    return `<span class="tooltip-wrapper">
      <button
        class="link-btn chunk-reference-btn"
        data-tooltip="${encodeURIComponent(getChunkTooltipHTML(chunk))}"
        data-chunk-id="${chunkId}"
      >
        <span class="chunk-number">${chunkNumber}</span>
      </button>
    </span>`
  })

  // Sanitizar el HTML final con los botones de chunks
  return DOMPurify.sanitize(htmlWithChunks, {
    ADD_ATTR: ['data-tooltip', 'data-chunk-id', 'style'],
    ADD_TAGS: ['button', 'span']
  })
})

// Contenido sin chunks para las acciones de la toolbar (copiar, descargar, email, imprimir)
const htmlContentWithoutChunks = computed(() => {
  if (!props.results || !props.results.generated_response) {
    return ''
  }
  const rawWithoutChunks = props.results.generated_response.replace(CHUNK_ID_REGEX, '')
  return DOMPurify.sanitize(processMarkdown(rawWithoutChunks))
})

const plainTextContentWithoutChunks = computed(() => {
  if (!htmlContentWithoutChunks.value) {
    return ''
  }
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContentWithoutChunks.value
  return tempDiv.textContent || tempDiv.innerText || ''
})

// METHODS
const toggleResults = (): void => {
  isResultsExpanded.value = !isResultsExpanded.value
}

return { __sfc: true,props, t, TEXT, ICON, infoIcon, isResultsExpanded, CHUNK_ID_REGEX, chunkIdMap, renderedContent, htmlContentWithoutChunks, plainTextContentWithoutChunks, toggleResults, SearchIAToolbarComponent, SearchIARelatedDocumentsComponent }
}

})